import { useState } from "react";
import { ExternalLink } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { safeHostname } from "@/marketing/lib/formatters";
import { deriveContentEmbed } from "@/marketing/lib/contentEmbed";
import { toContentPostItems } from "@/marketing/lib/contentPosts";
import type { ContentSubmission } from "@/marketing/models/campaignInfluencer";

interface ContentPostsProps {
  submissions?: ContentSubmission[] | undefined;
  // When provided, each post shows an editable Spark Ads code field (admin edit mode); otherwise it is read-only.
  onSaveSparkAdsCode?: (url: string, sparkAdsCode: string) => void;
  savingUrl?: string | undefined;
}

export function ContentPosts({ submissions, onSaveSparkAdsCode, savingUrl }: ContentPostsProps) {
  const items = toContentPostItems(submissions);
  if (items.length === 0) return null;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {items.map(({ url, sparkAdsCode }) => {
        const embed = deriveContentEmbed(url);
        return (
          <div key={url} className="tw-flex tw-flex-col tw-gap-1.5">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-py-1.5 tw-text-[12px] hover:tw-bg-accent"
            >
              <ExternalLink className="tw-h-3 tw-w-3 tw-flex-shrink-0 tw-text-muted-foreground" />
              <span className="tw-flex-1 tw-truncate">
                {url.replace(/^https?:\/\//, "")}
              </span>
              <span className="tw-flex-shrink-0 tw-text-[11px] tw-text-muted-foreground">
                {safeHostname(url)}
              </span>
            </a>
            {onSaveSparkAdsCode ? (
              <SparkAdsCodeEditor key={sparkAdsCode ?? ""} url={url} sparkAdsCode={sparkAdsCode} onSave={onSaveSparkAdsCode} saving={savingUrl === url} />
            ) : (
              sparkAdsCode && (
                <div className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-[11px]">
                  <span className="tw-font-medium tw-text-muted-foreground">Spark Ads</span>
                  <span className="tw-rounded tw-bg-muted tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-foreground">
                    {sparkAdsCode}
                  </span>
                </div>
              )
            )}
            {embed.embedSrc && (
              <div className="tw-overflow-hidden tw-rounded-md tw-border tw-border-border tw-bg-muted">
                <iframe
                  title={`Content preview · ${safeHostname(url)}`}
                  src={embed.embedSrc}
                  className={cn(
                    "tw-w-full tw-border-0",
                    embed.kind === "youtube" ? "tw-aspect-video" : "tw-h-[480px]",
                  )}
                  allow="encrypted-media"
                  referrerPolicy="strict-origin-when-cross-origin"
                  sandbox="allow-scripts allow-same-origin allow-popups"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SparkAdsCodeEditor({
  url,
  sparkAdsCode,
  onSave,
  saving,
}: {
  url: string;
  sparkAdsCode?: string | undefined;
  onSave: (url: string, sparkAdsCode: string) => void;
  saving?: boolean;
}) {
  const [value, setValue] = useState(sparkAdsCode ?? "");

  return (
    <div className="tw-inline-flex tw-items-center tw-gap-1.5">
      <span className="tw-text-[11px] tw-font-medium tw-text-muted-foreground">Spark Ads</span>
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Spark Ads code"
        aria-label="Spark Ads code"
        className="tw-h-7 tw-w-40 tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-2 tw-font-mono tw-text-[11px] focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-ring"
      />
      <button
        type="button"
        onClick={() => onSave(url, value.trim())}
        disabled={saving}
        className="tw-h-7 tw-rounded-md tw-border tw-border-border tw-px-2 tw-text-[11px] hover:tw-bg-accent disabled:tw-opacity-50"
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
